/* eslint-disable react/no-unknown-property */ // TODO add type for video tag Georgie
import { Check, Text } from '@myinterview/component-library';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from '../utils/device.utils';
import { SentryTrackingFn } from '../utils/sentry.utils';
import { PlayIcon } from './icons/PlayIcon';

export interface IDimensions {
  x: number;
  y:number;
  width:number;
  height: number;
}
interface IProps {
  blob: string;
  onSelectVideo: () => void
  isSelected: boolean;
  position: number;
  currentIndex: number;
  numberOfVideos: number;
  isPreviewMode: boolean;
  onPreviewMode: (dimensions: IDimensions) => void;
  myinterviewRef: React.RefObject<HTMLDivElement>;
}

const SWIPE_DISTANCE = 30;
const SLIDES_OFFSET = 80;

export const PreviewItem:React.FC<IProps> = ({
  blob, onSelectVideo, isSelected, position, currentIndex, numberOfVideos, isPreviewMode, onPreviewMode, myinterviewRef,
}) => {
  const { t } = useTranslation();
  const itemRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transformX = useRef<number>(0);
  const [_, setIsRendered] = useState(false);
  const startingPoint = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const isSwipe = useRef<boolean>(false);

  const handleVideoRef = useCallback((node: HTMLVideoElement) => {
    videoRef.current = node;
    videoRef.current?.load();
  }, []);

  const onPlayVideo = () => {
    if (videoRef.current && itemRef.current) {
      const { x, y, width, height } = videoRef.current.getBoundingClientRect();
      if (isMobile) {
        myinterviewRef?.current?.style.setProperty('--myinterview-widget-dynamic-overflow', 'initial');
      } else {
        videoRef.current.controls = true;
        videoRef.current.play();
      }
      onPreviewMode({ x, y, width, height });
    }
  };

  const onStopVideo = () => {
    if (videoRef.current) {
      videoRef.current.controls = false;
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (!isPreviewMode) {
      onStopVideo();
    }
  }, [isPreviewMode]);

  const calcTranformX = () => -(itemRef.current?.clientWidth || 0) * position + Math.max(-SLIDES_OFFSET, Math.min(position * SLIDES_OFFSET, SLIDES_OFFSET));
  transformX.current = calcTranformX();

  const handleItemRef = useCallback((node: HTMLDivElement) => {
    itemRef.current = node;
    transformX.current = calcTranformX();
    setIsRendered(true);
  }, []);

  const onVideoError = (e: any) => {
    SentryTrackingFn(e as any, { blobUrl: blob }, 'PREVIEW_VIDEO_FAILED', 'error');
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    startingPoint.current = 'pageX' in e ? { x: e.pageX, y: e.pageY } : { x: e.touches[0].pageX, y: e.touches[0].pageY };
    isSwipe.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (('buttons' in e && e.buttons === 1) || 'touches' in e) {
      const point = 'pageX' in e ? { x: e.pageX, y: e.pageY } : { x: e.touches[0].pageX, y: e.touches[0].pageY };
      isSwipe.current ||= Math.sqrt((point.y - startingPoint.current.y) ** 2 + (point.x - startingPoint.current.x) ** 2) > SWIPE_DISTANCE;
    }
  };

  const itemClassNames = classNames({
    'myinterview-widget-preview__item': true,
    'myinterview-widget-preview__item--current': !position,
    'myinterview-widget-preview__item--single-take': !isMobile && numberOfVideos === 1,
  });

  const videoWrapperClassNames = classNames({
    'myinterview-widget-preview__video-wrapper': true,
    'myinterview-widget-preview__video-wrapper--mobile': isMobile,
    'myinterview-widget-preview__video-wrapper--preview': isPreviewMode,
    'myinterview-widget-preview__video-wrapper--preview-mobile': isMobile && isPreviewMode,
    'myinterview-widget-preview__video-wrapper--current': isMobile && !position,
    'myinterview-widget-preview__video-wrapper--selected': isSelected,
  });

  const videoClassNames = classNames({
    'myinterview-widget-preview__video': true,
  });

  const checkmarkClassNames = classNames({
    'myinterview-widget-preview__selected-check-mark': true,
    'myinterview-widget-preview__selected-check-mark--selected': isSelected,
  });

  return (
    <div
      ref={handleItemRef}
      className={itemClassNames}
      style={isMobile ? {
        transform: `translate3d(calc(${transformX.current}px - ${position ? 'calc(var(--myinterview-widget-preview-transform-x) * 1.1)' : '0px'}), 0, 0) scale(${position ? '0.7' : '1'})`,
        zIndex: position < 0 ? position : position * -1,
      } : {}}
    >

      <div
        className={videoWrapperClassNames}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchMove={handleMouseMove}
        role="presentation"
      >
        <video
          ref={handleVideoRef}
          className={videoClassNames}
          onError={onVideoError}
          src={blob}
          playsInline
          controls={false}
          onContextMenu={(e) => { e.preventDefault(); return false; }}
          disablePictureInPicture
          controlsList="nodownload"
        />

        {!isPreviewMode && (
          <div
            className="myinterview-widget-preview__play-container"
            role="presentation"
            onMouseUp={() => !isSwipe.current && onPlayVideo()}
            onTouchEnd={() => !isSwipe.current && onPlayVideo()}
          >
            <div className="myinterview-widget-preview__play-button-wrapper">
              <PlayIcon playing={false} />
            </div>
          </div>
        )}
      </div>

      <div
        className="myinterview-widget-preview__number-wrapper"
        onMouseUp={onSelectVideo}
        onTouchEnd={onSelectVideo}
        role="presentation"
      >
        <div className={checkmarkClassNames}>
          <Check />
        </div>
        <Text size="S-Regular" color="neutral-60">
          { `${t('preview.take')} ${currentIndex + 1} / ${numberOfVideos}` }
        </Text>
      </div>

    </div>
  );
};
